import React, { useState } from 'react';
import { array, bool, func, object, string, number } from 'prop-types';

import { connect } from 'react-redux';

import { get } from 'lodash';

import moment from 'moment';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';

import AutoComplete from '../../components/FormFields/Autocomplete';
import DateField from '../../components/FormFields/DateField';
import EsmsDialog from '../../components/EsmsDialog';

import * as ServiceManagementActions from '../../actions/serviceManagementActions';
import * as UiActions from '../../actions/uiActions';

import classes from './AppointmentModal.module.css';
import 'react-datepicker/dist/react-datepicker.css';

const ValidationSchema = Yup.object().shape({
    primaryTechnician: Yup.string()
        .required(),
    scheduledDateTime: Yup.string()
        .required(),
    appointmentStatus: Yup.string()
        .required(),
    technicianNotes: Yup.string()
});

const NewAppointmentModal = ({
    appointmentStatuses,
    close,
    open,
    toggleLoading,
    clearErrors,
    dateFormat,
    timeFormat,
    timeIntervals,
    showTimeSelect,
    isLoading,
    technicianList,
    title,
    ...props
}) => {
    const [ sendComms, setSendComms ] = useState(false);
    const [ additionalTechs, setAdditionalTechs ] = useState([]);

    const dateTime = moment().local().startOf('hour').toDate();

    const formik = useFormik({
        initialValues: {
            primaryTechnician: '',
            scheduledDateTime: dateTime,
            appointmentStatus: '',
            technicianNotes:   '',
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                activeItem,
                createAppointment
            } = props;

            let isCreated;

            const body = {
                primaryTechnicianId: values.primaryTechnician.id,
                scheduledDateTime:   moment(values.scheduledDateTime).toISOString(),
                serviceCallId:       get(activeItem, 'id', ''),
                statusId:            values.appointmentStatus.id,
                otherTechnicianIds:  additionalTechs.map(tech => tech.id),
                technicianNotes:     values.technicianNotes
            };

            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createAppointment(body, {notify: sendComms});
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        close();
    };

    const handleChangeTime = (name) => {
        return (value, event) => {
            if (value) {
                formik.setFieldValue(name, value);
            }
            formik.setFieldTouched(name, true);
        };
    };

    const handleToggleAdditionalTechs = tech => {
        const techExists = additionalTechs.some(addTech => addTech.id === tech.id);

        if (techExists) {
            let updatedTechs = additionalTechs.filter(addTech => addTech.id !== tech.id);

            setAdditionalTechs(updatedTechs);
            return;
        }

        setAdditionalTechs([...additionalTechs, tech]);
    };

    const handleSendComms = () => {
        setSendComms(!sendComms);
    };

    if (!technicianList.length) return null;


    const filteredTechnicians = technicianList[0].items.filter(technician => technician.isActive === true);

    const filteredTechniciansList = [
        {
            items:     [...filteredTechnicians],
            name:      'Technicians',
            itemProps: {
                type: 'technicians'
            }
        }
    ];

    return (
        <EsmsDialog
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            onRendered={formik.handleReset}
            isLoading={isLoading}
            confirmText="Create"
            className={classes.AppointmentModal}
        >
            <div className={classes.AppointmentModalWrapper}>
                <div className={classes.FormContainer}>
                    <FormControl className={classes.FullWidth}>
                        <AutoComplete
                            grouped
                            label="Primary Technician"
                            name="primaryTechnician"
                            classes={{
                                inputRoot: classes.inputRoot
                            }}
                            onChange={formik.handleChange}
                            className={classes.autoComplete}
                            options={filteredTechniciansList}
                            value={formik.values.primaryTechnician}
                            error={Boolean(formik.errors.primaryTechnician) && Boolean(formik.touched.primaryTechnician)}
                            helperText={Boolean(formik.touched.primaryTechnician) && formik.errors.primaryTechnician}
                        />
                    </FormControl>
                    <FormControl className={classes.FullWidth}>
                        <div className={classes.DatePicker}>
                            <DateField
                                label="Schedule Date/Time"
                                onChange={handleChangeTime('scheduledDateTime')}
                                margin="dense"
                                name="appointmentTimestamp"
                                dateFormat={dateFormat}
                                showTimeSelect={showTimeSelect}
                                timeFormat={timeFormat}
                                value={formik.values.scheduledDateTime}
                            />
                        </div>
                    </FormControl>
                    <FormControl className={classes.FullWidth}>
                        <AutoComplete
                            grouped
                            label="Appointment Status"
                            name="appointmentStatus"
                            classes={{
                                inputRoot: classes.inputRoot
                            }}
                            onChange={formik.handleChange}
                            className={classes.autoComplete}
                            options={appointmentStatuses}
                            value={formik.values.appointmentStatus}
                            error={Boolean(formik.errors.appointmentStatus) && Boolean(formik.touched.appointmentStatus)}
                            helperText={Boolean(formik.touched.appointmentStatus) && formik.errors.appointmentStatus}
                        />
                    </FormControl>
                </div>
                <div className={classes.TechnicianContainer}>
                    <div className={classes.ContainerLabel}>
                        Additional Technicians
                    </div>
                    <div className={classes.TechnicianList}>
                        <ul className={classes.List}>
                            {
                                filteredTechniciansList[0].items.map((technician, index) =>
                                    <MenuItem
                                        key={index}
                                    >
                                        <Checkbox
                                            classes={{
                                                root: classes.checkbox
                                            }}
                                            color="primary"
                                            onClick={() => handleToggleAdditionalTechs(technician)}
                                        />
                                        <span className={classes.TechnicianNames}>{technician.name}</span>
                                    </MenuItem>
                                )
                            }
                        </ul>
                    </div>
                </div>
                <div className={classes.NotesContainer}>
                    <div className={classes.ContainerLabel}>
                        Technician Notes
                    </div>
                    <FormControl className={classes.FullWidth}>
                        <TextField
                            multiline
                            rows={8}
                            value={formik.values.technicianNotes}
                            onChange={formik.handleChange}
                            name="technicianNotes"
                            className={classes.Comments}
                            variant="outlined"
                        />
                    </FormControl>
                </div>
            </div>
            <div className={classes.SendCommsContainer}>
                <FormControl className={classes.checkboxWrapper}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendComms}
                                onChange={handleSendComms}
                                name="sendCommunications"
                                color="primary"
                            />
                        }
                        label="Send Client communications on Create (if applicable)"
                        className={classes.checkboxLabel}
                    />
                </FormControl>
            </div>
        </EsmsDialog>
    );
};

NewAppointmentModal.defaultProps = {
    activeItem:     {},
    close:          () => {},
    dateFormat:     'yyyy-MM-dd h:mm a',
    isLoading:      false,
    open:           false,
    showTimeSelect: true,
    technicianList: [],
    timeFormat:     'h:mm a',
    timeIntervals:  15,
    title:          ''
};

NewAppointmentModal.propTypes = {
    activeItem:     object,
    close:          func,
    open:           bool,
    dateFormat:     string,
    isLoading:      bool,
    showTimeSelect: bool,
    technicianList: array,
    timeFormat:     string,
    timeIntervals:  number,
    title:          string
};

const mapStateToProps = state => {
    return {
        activeItem:     state.svcManagement.activeItem,
        isLoading:      state.ui.isLoading,
        technicianList: state.svcManagement.technicianList
    };
};

const mapDispatchToProps = {
    ...ServiceManagementActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(NewAppointmentModal);
